import { DEFAULT_PITCH, PITCH_RANGE, SECTION_LABELS, defaultTrackSettings, INSTRUMENT_LABELS } from '../../domain/factories';
import {
  INSTRUMENTS,
  MAX_SECTION_BARS,
  MAX_SONG_BARS,
  STEPS_PER_BAR,
  type GeneratorParams,
  type Instrument,
  type MusicalKey,
  type NoteEvent,
  type Pattern,
  type Section,
  type SectionKind,
  type Track,
} from '../../domain/schemas';
import { Rng } from '../random';
import { chordTones, degreeToMidi } from '../theory';
import { GENRES, type GenreProfile } from './genres';

/**
 * Deterministic, rule-based procedural composer (no machine learning, no network).
 * Every random decision comes from a seeded PRNG forked per section and per part, so
 * the same GeneratorParams always produce exactly the same composition.
 */
export interface Composition {
  params: GeneratorParams;
  settings: { bpm: number; swing: number; key: MusicalKey };
  tracks: Track[];
  patterns: Pattern[];
  sections: Section[];
  bars: number;
  durationSec: number;
}

const BARS_PER_PATTERN = 4;
const PATTERN_STEPS = (STEPS_PER_BAR * BARS_PER_PATTERN) as 64;

interface Roles {
  drums: number;
  kick: boolean;
  bass: number;
  melody: number;
  counter: number;
  pad: number;
  build?: boolean;
}

const ROLES: Record<SectionKind, Roles> = {
  intro: { drums: 0.35, kick: false, bass: 0, melody: 0.6, counter: 0.5, pad: 1 },
  verse: { drums: 0.75, kick: true, bass: 0.8, melody: 0.6, counter: 0.6, pad: 0.7 },
  buildup: { drums: 0.6, kick: false, bass: 0, melody: 0.7, counter: 0.5, pad: 0.8, build: true },
  drop: { drums: 1, kick: true, bass: 1, melody: 1, counter: 1, pad: 0.6 },
  breakdown: { drums: 0.15, kick: false, bass: 0.4, melody: 0.8, counter: 0.6, pad: 1 },
  outro: { drums: 0.5, kick: true, bass: 0.5, melody: 0.5, counter: 0, pad: 1 },
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const r2 = (v: number) => Math.round(v * 100) / 100;
const vel = (v: number) => r2(clamp(v, 0.05, 1));

/** Shifts a MIDI note by octaves until it sits inside the instrument's range. */
function fit(instrument: Instrument, midi: number): number {
  const [low, high] = PITCH_RANGE[instrument];
  let m = midi;
  while (m < low) m += 12;
  while (m > high) m -= 12;
  return m;
}

interface Context {
  profile: GenreProfile;
  params: GeneratorParams;
  progression: number[];
  velocityScale: number;
  hatBoost: number;
  melodyScale: number;
}

function drumsFor(ctx: Context, roles: Roles, rng: Rng): { notes: Partial<Record<Instrument, NoteEvent[]>>; kicks: number[][] } {
  const { profile, params } = ctx;
  const { drums } = profile;
  const I = params.intensity;
  const C = params.complexity;
  const out: Record<string, NoteEvent[]> = { kick: [], snare: [], clap: [], hats: [], perc: [] };
  const kicks: number[][] = [];
  const add = (instrument: Instrument, step: number, velocity: number, length = 1, offset = 0, pitch = DEFAULT_PITCH[instrument]) =>
    out[instrument]!.push({ step, pitch, velocity: vel(velocity * ctx.velocityScale), length, offset });

  const kickExtras = drums.kickExtra.filter(() => rng.chance(0.15 + I * 0.55));
  for (let bar = 0; bar < BARS_PER_PATTERN; bar++) {
    const o = bar * STEPS_PER_BAR;
    const last = bar === BARS_PER_PATTERN - 1;
    const barKicks: number[] = [];
    if (roles.kick) {
      for (const s of [...drums.kick, ...kickExtras].sort((a, b) => a - b)) {
        if (roles.build) continue;
        barKicks.push(s);
        add('kick', o + s, s === 0 ? 1 : 0.85);
      }
    }
    kicks.push(barKicks);
    if (roles.build) continue;
    // Backbeat
    if (roles.drums >= 0.5) {
      for (const s of drums.snare) add('snare', o + s, 1);
      for (const s of drums.clap) add('clap', o + s, 0.95);
      if (drums.snare.length && C > 0.3) {
        for (let s = 1; s < STEPS_PER_BAR; s += 2) if (!drums.snare.includes(s) && rng.chance(C * 0.18)) add('snare', o + s, 0.3);
      }
      if (last && C > 0.55) {
        // End-of-phrase fill.
        const fill = drums.snare.length ? 'snare' : 'clap';
        for (const s of [13, 14, 15]) if (rng.chance(0.4 + C * 0.4)) add(fill, o + s, 0.45 + (s - 13) * 0.2);
      }
    }
    // Hats
    const hatChance = clamp(0.35 + roles.drums * 0.65 + ctx.hatBoost, 0, 1);
    for (let s = 0; s < STEPS_PER_BAR; s += drums.hatEvery) {
      if (!rng.chance(hatChance)) continue;
      const accent = s % 4 === 0 ? 0.65 : 0.45;
      if (drums.hatRolls > 0 && roles.drums >= 0.7 && s % 4 === 2 && rng.chance(drums.hatRolls * (0.4 + C))) {
        if (rng.chance(0.5)) {
          // 32nd-note roll across an 8th.
          for (const [ds, off] of [[0, 0], [0, 0.5], [1, 0], [1, 0.5]] as const) add('hats', o + s + ds, 0.4 + ds * 0.1, 0.5, off);
        } else {
          // 16th-note triplet roll across an 8th.
          for (const [ds, off] of [[0, 0], [1, -0.33], [1, 0.33]] as const) add('hats', o + s + ds, 0.42, 0.5, off);
        }
        continue;
      }
      add('hats', o + s, accent, s === 14 && profile.drums.hatEvery === 2 && rng.chance(0.3) ? 2 : 1);
    }
    if (I > 0.6 && drums.hatEvery >= 2 && roles.drums >= 0.7) {
      for (let s = 1; s < STEPS_PER_BAR; s += 2) if (rng.chance((I - 0.6) * 1.2 + ctx.hatBoost)) add('hats', o + s, 0.3);
    }
    // Percussion
    if (roles.drums >= 0.5) {
      for (const s of drums.perc) if (rng.chance(0.65 + I * 0.35)) add('perc', o + s, s % 4 === 2 ? 0.8 : 0.6, 1, 0, rng.pick([50, 55, 57]));
      if (!drums.perc.length && C > 0.6) {
        for (let s = 2; s < STEPS_PER_BAR; s += 4) if (rng.chance((C - 0.6) * 1.5)) add('perc', o + s + 1, 0.5, 1, 0, 52);
      }
    }
  }
  if (roles.build) {
    // Accelerating snare roll (quarters → 8ths → 16ths) with rising velocity and hats.
    for (let step = 0; step < PATTERN_STEPS; step++) {
      const p = step / PATTERN_STEPS;
      const every = p < 0.5 ? 4 : p < 0.75 ? 2 : 1;
      if (step % every === 0) add('snare', step, 0.3 + 0.7 * p);
      if (step % (p < 0.5 ? 2 : 1) === 0) add('hats', step, 0.3 + 0.3 * p);
    }
  }
  const notes: Partial<Record<Instrument, NoteEvent[]>> = {};
  for (const [k, v] of Object.entries(out)) if (v.length) notes[k as Instrument] = v;
  return { notes, kicks };
}

function bassFor(ctx: Context, roles: Roles, kicks: number[][], rng: Rng): NoteEvent[] {
  if (roles.bass <= 0) return [];
  const { profile, params } = ctx;
  const { root, scale } = params.key;
  const C = params.complexity;
  const notes: NoteEvent[] = [];
  const rootOf = (bar: number) => fit('bass808', degreeToMidi(root, scale, ctx.progression[bar % ctx.progression.length]!, profile.bass.octave));
  for (let bar = 0; bar < BARS_PER_PATTERN; bar++) {
    const o = bar * STEPS_PER_BAR;
    const r = rootOf(bar);
    const degree = ctx.progression[bar]!;
    const fifth = fit('bass808', degreeToMidi(root, scale, degree + 4, profile.bass.octave));
    const v = vel((0.85 + 0.15 * roles.bass) * ctx.velocityScale);
    if (profile.bass.style === 'walking') {
      notes.push({ step: o, pitch: r, velocity: v, length: 6, offset: 0 });
      notes.push({ step: o + 8, pitch: fifth, velocity: vel(v * 0.9), length: 4, offset: 0 });
      if (rng.chance(0.3 + C * 0.5)) {
        // Diatonic approach note one scale step from the next chord's root.
        const nextDegree = ctx.progression[(bar + 1) % ctx.progression.length]!;
        const approach = degreeToMidi(root, scale, nextDegree + (rng.chance(0.5) ? 1 : -1), profile.bass.octave);
        notes.push({ step: o + 14, pitch: fit('bass808', approach), velocity: vel(v * 0.8), length: 2, offset: 0 });
      }
      continue;
    }
    const hits = kicks[bar]?.length ? kicks[bar]! : [0, 8];
    hits.forEach((s, i) => {
      const nextHit = hits[i + 1] ?? STEPS_PER_BAR;
      let pitch = r;
      if (i > 0 && rng.chance(C * 0.35)) pitch = rng.chance(0.5) ? fifth : fit('bass808', r + 12);
      const length = profile.bass.style === 'syncopated' ? Math.min(2, nextHit - s) : Math.min(8, nextHit - s);
      notes.push({ step: o + s, pitch, velocity: v, length: Math.max(1, length), offset: 0 });
    });
  }
  return notes;
}

/** Builds a one-bar motif as (step, length, chord-relative scale-degree offset) triples. */
function motif(style: GenreProfile['melody']['style'], density: number, rng: Rng): [number, number, number][] {
  const out: [number, number, number][] = [];
  if (style === 'arp') {
    const shapes = [
      [0, 2, 4, 7, 4, 2],
      [0, 4, 7, 4],
      [7, 4, 2, 0, 2, 4],
      [0, 2, 4, 2, 7, 4, 2, 4],
    ];
    const shape = rng.pick(shapes);
    let k = 0;
    for (let s = 0; s < STEPS_PER_BAR; s++) {
      if (s % 2 === 1 && !rng.chance(density / 16)) continue;
      out.push([s, 1, shape[k++ % shape.length]!]);
    }
    return out;
  }
  if (style === 'stab') {
    const rhythms = [
      [0, 3, 6, 8, 10, 13, 14],
      [0, 2, 3, 6, 8, 11, 12, 14],
      [0, 3, 4, 7, 8, 10, 12, 15],
      [0, 3, 6, 9, 12, 14],
    ];
    const rhythm = rng.pick(rhythms).filter((s, i) => i < Math.max(3, Math.round(density)) || s === 0);
    for (const s of rhythm) out.push([s, 1, rng.pick([0, 0, 4, 7, 7, 2, -1])]);
    return out;
  }
  // phrase: weighted onsets, chord tones on strong beats, stepwise motion elsewhere.
  const count = clamp(Math.round(density), 2, 10);
  const weights = Array.from({ length: STEPS_PER_BAR }, (_, s) => (s % 4 === 0 ? 4 : s % 2 === 0 ? 2 : 0.7));
  const chosen = new Set<number>([0]);
  while (chosen.size < count) {
    const total = weights.reduce((a, b) => a + b, 0);
    let x = rng.float() * total;
    let pick = 0;
    for (; pick < STEPS_PER_BAR - 1; pick++) {
      x -= weights[pick]!;
      if (x <= 0) break;
    }
    chosen.add(pick);
  }
  const steps = [...chosen].sort((a, b) => a - b);
  let degree = rng.pick([0, 2, 4]);
  steps.forEach((s, i) => {
    const next = steps[i + 1] ?? STEPS_PER_BAR;
    if (s % 4 === 0) degree = [0, 2, 4, 7].reduce((best, d) => (Math.abs(d - degree) < Math.abs(best - degree) ? d : best), 0);
    else degree = clamp(degree + rng.pick([-1, 1, 1, -2, 2]), -3, 9);
    out.push([s, Math.min(4, next - s), degree]);
  });
  return out;
}

function melodyFor(ctx: Context, instrument: Instrument, style: GenreProfile['melody']['style'], density: number, octave: number, roleLevel: number, rng: Rng): NoteEvent[] {
  if (roleLevel <= 0 || density <= 0) return [];
  const { root, scale } = ctx.params.key;
  const C = ctx.params.complexity;
  const d = density * (0.6 + C * 0.8) * roleLevel * ctx.melodyScale;
  const a = motif(style, d, rng.fork('a'));
  const b = C > 0.45 ? motif(style, d, rng.fork('b')) : a;
  const notes: NoteEvent[] = [];
  for (let bar = 0; bar < BARS_PER_PATTERN; bar++) {
    const chord = ctx.progression[bar]!;
    const m = bar % 2 === 1 ? b : a;
    for (const [s, length, offset] of m) {
      const pitch = fit(instrument, degreeToMidi(root, scale, chord + offset, octave));
      const accent = s % 4 === 0 ? 1 : 0.8;
      notes.push({ step: bar * STEPS_PER_BAR + s, pitch, velocity: vel(accent * (0.7 + 0.3 * roleLevel) * ctx.velocityScale), length, offset: 0 });
    }
  }
  return notes;
}

function padFor(ctx: Context, roles: Roles): NoteEvent[] {
  if (ctx.profile.pad.style === 'none' || roles.pad <= 0) return [];
  const { root, scale } = ctx.params.key;
  const notes: NoteEvent[] = [];
  for (let bar = 0; bar < BARS_PER_PATTERN; bar++) {
    const tones = chordTones(root, scale, ctx.progression[bar]!, 3, ctx.profile.sevenths ? 4 : 3).map((m) => fit('pad', m));
    for (const pitch of [...new Set(tones)]) {
      notes.push({ step: bar * STEPS_PER_BAR, pitch, velocity: vel(0.5 * roles.pad * ctx.velocityScale + 0.1), length: STEPS_PER_BAR, offset: 0 });
    }
  }
  return notes;
}

function patternFor(ctx: Context, kind: SectionKind, base: Rng): Pattern {
  const roles = ROLES[kind];
  const rng = base.fork(kind);
  const { notes: drumNotes, kicks } = drumsFor(ctx, roles, rng.fork('drums'));
  const tracks: Partial<Record<Instrument, NoteEvent[]>> = { ...drumNotes };
  const bass = bassFor(ctx, roles, kicks, rng.fork('bass'));
  if (bass.length) tracks.bass808 = bass;
  const { melody, counter } = ctx.profile;
  const lead = melodyFor(ctx, melody.instrument, melody.style, melody.density, melody.octave, roles.melody, rng.fork('melody'));
  if (lead.length) tracks[melody.instrument] = lead;
  if (counter && counter.density > 0) {
    const c = melodyFor(ctx, counter.instrument, 'stab', counter.density, counter.octave, roles.counter, rng.fork('counter'));
    if (c.length) tracks[counter.instrument] = [...(tracks[counter.instrument] ?? []), ...c];
  }
  const pad = padFor(ctx, roles);
  if (pad.length) tracks.pad = pad;
  for (const [k, v] of Object.entries(tracks)) tracks[k as Instrument] = v!.sort((x, y) => x.step - y.step || x.pitch - y.pitch || x.offset - y.offset).slice(0, 512);
  return { id: `pat_gen_${kind}`, name: SECTION_LABELS[kind], lengthSteps: PATTERN_STEPS, tracks };
}

/** Splits `totalBars` over the genre form; drops minor sections when the song is short. */
export function planSections(profile: GenreProfile, totalBars: number): { kind: SectionKind; bars: number }[] {
  const keepOrder: SectionKind[] = ['drop', 'verse', 'intro', 'outro', 'breakdown', 'buildup'];
  const form = [...profile.form];
  const minBars = 2;
  while (form.length > 1) {
    const weight = form.reduce((s, [, w]) => s + w, 0);
    const smallest = Math.min(...form.map(([, w]) => w));
    if ((smallest / weight) * totalBars >= minBars) break;
    // Remove the least important section (last occurrence of the lowest-priority kind).
    const victimKind = [...keepOrder].reverse().find((k) => form.some(([kind]) => kind === k))!;
    const index = form.map(([k]) => k).lastIndexOf(victimKind);
    if (form.filter(([k]) => k === 'drop').length === 1 && victimKind === 'drop') break;
    form.splice(index, 1);
  }
  const weight = form.reduce((s, [, w]) => s + w, 0);
  const plan = form.map(([kind, w]) => ({ kind, bars: Math.max(minBars, Math.round(((w / weight) * totalBars) / 2) * 2) }));
  // Fix rounding drift on the longest section so the total is exact.
  let diff = totalBars - plan.reduce((s, p) => s + p.bars, 0);
  const sorted = [...plan].sort((a, b) => b.bars - a.bars);
  for (const p of sorted) {
    if (diff === 0) break;
    const change = diff > 0 ? diff : Math.max(diff, -(p.bars - 1));
    p.bars += change;
    diff -= change;
  }
  // Split oversized sections so each stays within the per-section limit.
  return plan.flatMap((p) => {
    const parts: { kind: SectionKind; bars: number }[] = [];
    let remaining = p.bars;
    while (remaining > 0) {
      const bars = Math.min(MAX_SECTION_BARS, remaining);
      parts.push({ kind: p.kind, bars });
      remaining -= bars;
    }
    return parts;
  }).filter((p) => p.bars > 0);
}

export function barsForDuration(durationSec: number, bpm: number): number {
  return clamp(Math.round((durationSec * bpm) / 240), 2, MAX_SONG_BARS);
}

function mixFor(params: GeneratorParams, profile: GenreProfile, existing: readonly Track[] | undefined): Track[] {
  const I = params.intensity;
  return INSTRUMENTS.map((instrument) => {
    const defaults = defaultTrackSettings(instrument);
    const m = profile.mix[instrument] ?? {};
    let volumeDb = defaults.volumeDb + (m.volumeDb !== undefined ? m.volumeDb - defaults.volumeDb : 0);
    let drive = m.drive ?? defaults.effects.drive;
    let cutoffHz = m.cutoffHz ?? defaults.effects.cutoffHz;
    let reverbSend = m.reverbSend ?? defaults.effects.reverbSend;
    const delaySend = m.delaySend ?? defaults.effects.delaySend;
    const distorted = instrument === 'kick' || instrument === 'bass808' || instrument === 'cowbell';
    if (distorted && drive > 0) drive += (I - 0.5) * 0.3;
    switch (params.mood) {
      case 'aggressive':
        if (distorted) drive += 0.15;
        break;
      case 'chill':
        drive *= 0.5;
        cutoffHz = Math.min(cutoffHz, instrument === 'hats' ? 8000 : 6000);
        reverbSend += 0.1;
        break;
      case 'dark':
        reverbSend += instrument === 'kick' || instrument === 'bass808' ? 0 : 0.1;
        if (instrument === 'lead' || instrument === 'pad' || instrument === 'cowbell') cutoffHz *= 0.7;
        break;
      case 'melancholic':
        if (instrument === 'pad') volumeDb += 2;
        reverbSend += 0.08;
        break;
      case 'energetic':
        if (instrument === 'hats') volumeDb += 1;
        break;
    }
    const focus = params.focus[instrument];
    if (focus === 'heavy') {
      volumeDb += 3;
      if (distorted) drive += 0.2;
    } else if (focus === 'subtle') {
      volumeDb -= 5;
    }
    const previous = existing?.find((t) => t.instrument === instrument);
    return {
      id: previous?.id ?? `trk_${instrument}`,
      name: previous?.name ?? INSTRUMENT_LABELS[instrument],
      instrument,
      volumeDb: r2(clamp(volumeDb, -60, 6)),
      pan: m.pan ?? defaults.pan,
      muted: false,
      solo: false,
      effects: {
        drive: r2(clamp(drive, 0, 1)),
        cutoffHz: Math.round(clamp(cutoffHz, 80, 20000)),
        delaySend: r2(clamp(delaySend, 0, 1)),
        reverbSend: r2(clamp(reverbSend, 0, 1)),
      },
      // Regenerating keeps the user's sample instruments.
      sample: previous?.sample ?? null,
    };
  });
}

export function compose(params: GeneratorParams, existingTracks?: readonly Track[]): Composition {
  const profile = GENRES[params.genre];
  const rng = new Rng(params.seed);
  const progression = rng.fork('harmony').pick(profile.progressions);
  const moodVelocity = params.mood === 'aggressive' ? 1.1 : params.mood === 'chill' ? 0.85 : 1;
  const ctx: Context = {
    profile,
    params,
    progression,
    velocityScale: (0.85 + params.intensity * 0.3) * moodVelocity,
    hatBoost: params.mood === 'energetic' ? 0.15 : params.mood === 'chill' ? -0.1 : 0,
    melodyScale: params.mood === 'melancholic' ? 0.75 : 1,
  };
  const totalBars = barsForDuration(params.durationSec, params.bpm);
  const plan = planSections(profile, totalBars);
  const patterns = new Map<SectionKind, Pattern>();
  for (const { kind } of plan) if (!patterns.has(kind)) patterns.set(kind, patternFor(ctx, kind, rng.fork('patterns')));
  const sections: Section[] = plan.map((p, i) => ({
    id: `sec_gen_${i + 1}`,
    name: SECTION_LABELS[p.kind],
    kind: p.kind,
    patternId: patterns.get(p.kind)!.id,
    bars: p.bars,
  }));
  const swing = r2(clamp(profile.swing + (params.mood === 'chill' ? 0.05 : 0), 0, 0.5));
  return {
    params,
    settings: { bpm: params.bpm, swing, key: params.key },
    tracks: mixFor(params, profile, existingTracks),
    patterns: [...patterns.values()],
    sections,
    bars: totalBars,
    durationSec: r2((totalBars * 240) / params.bpm),
  };
}

/** Sensible defaults for a genre; any field can be overridden. */
export function defaultParams(genre: GeneratorParams['genre'], overrides: Partial<GeneratorParams> = {}): GeneratorParams {
  const profile = GENRES[genre];
  return {
    genre,
    bpm: profile.bpm,
    key: profile.key,
    mood: profile.mood,
    intensity: 0.7,
    complexity: 0.5,
    durationSec: 60,
    seed: 1,
    focus: {},
    ...overrides,
  };
}
